import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { SearchablePicker } from '@/components/ui/SearchablePicker';
import { useAuth } from '@/hooks/useAuth';
import { useProperties, usePropertyImages } from '@/hooks/data/useProperties';
import { apiFetch } from '@/lib/api';
import { buildFlyerPhotos, generateFlyerBlob } from '@/lib/flyerPdf';
import type { FlyerData } from '@/components/design/FlyerDocument';
import { useUploadMedia } from '@/hooks/data/useMedia';
import {
  SocialTemplate,
  type DesignType,
  type DesignFormat,
  type SocialTemplateData,
} from '@/components/design/SocialTemplate';
import {
  apiImageAsDataUrl,
  publicAssetAsDataUrl,
  generateQrDataUrl,
  listingUrlFor,
  exportNodeToPng,
  downloadDataUrl,
  dataUrlToFile,
} from '@/lib/designExport';
import { formatMoney, formatNumber } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/design')({ component: DesignPage });

const TYPES: DesignType[] = ['just_listed', 'just_sold', 'price_reduction', 'open_house'];

const TYPE_META: Record<DesignType, { badge: string; accent: string }> = {
  just_listed: { badge: 'NEU IM ANGEBOT', accent: '#c9a227' },
  just_sold: { badge: 'VERKAUFT', accent: '#3b8a4a' },
  price_reduction: { badge: 'PREIS REDUZIERT', accent: '#b04a4a' },
  open_house: { badge: 'OPEN HOUSE', accent: '#3a6ea5' },
};

const AGENT_PHONE_FALLBACK = '+43 1 905 00 30';

type PageFormat = DesignFormat | 'flyer';

function fmtPrice(p: number | null | undefined): string | null {
  if (!p) return null;
  return formatMoney(Number(p));
}

function DesignPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { data: properties = [] } = useProperties({ limit: 5000 });
  const upload = useUploadMedia();

  const [propertyId, setPropertyId] = useState('');
  const [type, setType] = useState<DesignType>('just_listed');
  const [format, setFormat] = useState<PageFormat>('square');
  const [photo, setPhoto] = useState<string | null>(null);
  const [logo, setLogo] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  // Flyer-specific state
  const { data: propertyImages = [] } = usePropertyImages(propertyId || undefined);
  const [flyerPhotos, setFlyerPhotos] = useState<string[]>([]);
  const [flyerUrl, setFlyerUrl] = useState<string | null>(null);
  const [flyerBusy, setFlyerBusy] = useState(false);

  const property = useMemo(
    () => properties.find((p) => p.id === propertyId),
    [properties, propertyId],
  );

  const propertyOptions = useMemo(
    () =>
      properties.map((p) => ({
        value: p.id,
        label: p.address ?? p.city ?? '—',
        secondary: [p.postal_code, p.city].filter(Boolean).join(' · '),
      })),
    [properties],
  );

  // Load logo once on mount
  useEffect(() => {
    publicAssetAsDataUrl('/brand/logo-full.png').then(setLogo);
  }, []);

  // Load photo + QR whenever propertyId changes
  useEffect(() => {
    let cancelled = false;
    if (!propertyId) {
      setPhoto(null);
      setQr(null);
      return;
    }
    setPreparing(true);
    (async () => {
      const photoData = await apiImageAsDataUrl(`/properties/${propertyId}/primary-image`);

      let qrData: string | null = null;
      try {
        const res = await apiFetch(`/properties/${propertyId}/share-token`, { method: 'POST' });
        if (res.ok) {
          const j = (await res.json()) as { token?: string | null; url?: string | null };
          const token = j.token;
          qrData = await generateQrDataUrl(
            token ? listingUrlFor(token) : 'https://4m-immo.at',
          );
        } else {
          qrData = await generateQrDataUrl('https://4m-immo.at');
        }
      } catch {
        qrData = await generateQrDataUrl('https://4m-immo.at');
      }

      if (!cancelled) {
        setPhoto(photoData);
        setQr(qrData);
        setPreparing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const data: SocialTemplateData = useMemo(() => {
    const meta: string[] = [];
    if (property?.property_size) meta.push(`${property.property_size} m²`);
    if (property?.rooms_count) meta.push(`${property.rooms_count} ${t('design.rooms', 'Zimmer')}`);
    return {
      badge: TYPE_META[type].badge,
      accent: TYPE_META[type].accent,
      address: property?.address ?? property?.city ?? '—',
      locality: [property?.postal_code, property?.city, property?.district]
        .filter(Boolean)
        .join(' · '),
      price: fmtPrice(property?.purchase_price),
      meta,
      agentName: profile?.full_name ?? '4M Immobilien',
      agentPhone: profile?.phone ?? AGENT_PHONE_FALLBACK,
      photoDataUrl: photo,
      logoDataUrl: logo,
      qrDataUrl: qr,
    };
  }, [property, type, photo, logo, qr, profile, t]);

  // Social-format guard: treat 'flyer' as 'square' for dims/preview/export
  const socialFormat: DesignFormat = format === 'flyer' ? 'square' : format;

  const dims = socialFormat === 'square' ? { w: 1080, h: 1080 } : { w: 1080, h: 1920 };
  const previewScale = socialFormat === 'square' ? 0.42 : 0.28;
  const filenameBase = `Design_${type}_${format}_${(property?.address ?? 'immo').replace(/[^\w-]+/g, '_')}`;

  // Load flyer photos whenever propertyId or images change
  useEffect(() => {
    let cancelled = false;
    if (!propertyId) { setFlyerPhotos([]); return; }
    buildFlyerPhotos(propertyId, propertyImages).then((ps) => {
      if (!cancelled) setFlyerPhotos(ps);
    });
    return () => { cancelled = true; };
  }, [propertyId, propertyImages]);

  const flyerData: FlyerData = useMemo(() => ({
    badge: data.badge,
    accent: data.accent,
    address: data.address,
    locality: data.locality,
    price: data.price,
    meta: data.meta,
    agentName: data.agentName,
    agentPhone: data.agentPhone,
    photos: flyerPhotos,
    qrDataUrl: qr,
  }), [data, flyerPhotos, qr]);

  const doExport = async (): Promise<string | null> => {
    if (!exportRef.current) return null;
    return exportNodeToPng(exportRef.current, dims.w, dims.h);
  };

  const onDownload = async () => {
    const png = await doExport();
    if (!png) return;
    downloadDataUrl(png, `${filenameBase}.png`);
  };

  const onSave = async () => {
    const png = await doExport();
    if (!png) return;
    try {
      await upload.mutateAsync({ file: dataUrlToFile(png, `${filenameBase}.png`), scope: 'personal' });
      toast.success(t('design.saved', 'In Mediathek gespeichert.'));
    } catch {
      toast.error(t('design.save_failed', 'Speichern fehlgeschlagen.'));
    }
  };

  // Flyer actions
  const makeFlyerBlob = () => generateFlyerBlob(flyerData);

  const onFlyerPreview = async () => {
    setFlyerBusy(true);
    try {
      const blob = await makeFlyerBlob();
      setFlyerUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } finally {
      setFlyerBusy(false);
    }
  };

  const onFlyerDownload = async () => {
    const blob = await makeFlyerBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenameBase}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onFlyerSave = async () => {
    const blob = await makeFlyerBlob();
    try {
      await upload.mutateAsync({
        file: new File([blob], `${filenameBase}.pdf`, { type: 'application/pdf' }),
        scope: 'personal',
      });
      toast.success(t('design.saved', 'In Mediathek gespeichert.'));
    } catch {
      toast.error(t('design.save_failed', 'Speichern fehlgeschlagen.'));
    }
  };

  const ready = !!propertyId && !preparing;

  return (
    <section className="space-y-6">
      <header>
        <h2 className="font-display text-2xl text-ink">
          {t('design.title', 'Design-Center')}
        </h2>
        <p className="text-sm text-ink-3">
          {t('design.subtitle', 'Erstellen Sie Social-Media-Grafiken für Ihre Immobilien.')}
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        {/* Controls */}
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-3">
              {t('design.property', 'Immobilie')}
            </label>
            <SearchablePicker
              options={propertyOptions}
              value={propertyId}
              onChange={setPropertyId}
              placeholder={t('design.pick_property', 'Immobilie wählen …')}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-3">
              {t('design.type', 'Typ')}
            </label>
            <Select value={type} onChange={(e) => setType(e.target.value as DesignType)}>
              {TYPES.map((x) => (
                <option key={x} value={x}>
                  {t(`design.type_${x}`, TYPE_META[x].badge)}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-3">
              {t('design.format', 'Format')}
            </label>
            <div className="inline-flex rounded-lg border border-line p-0.5">
              {(['square', 'story', 'flyer'] as PageFormat[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFormat(f)}
                  className={`rounded-md px-3 py-1 text-sm ${
                    format === f ? 'bg-ink text-paper' : 'text-ink-3 hover:bg-paper-2'
                  }`}
                >
                  {f === 'square'
                    ? t('design.square', 'Quadrat')
                    : f === 'story'
                      ? t('design.story', 'Story')
                      : t('design.flyer', 'Flyer (A4)')}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            {format === 'flyer' ? (
              <>
                <Button onClick={onFlyerPreview} disabled={!ready || flyerBusy}>
                  {t('design.flyer_preview', 'Vorschau erstellen')}
                </Button>
                <Button onClick={onFlyerDownload} disabled={!ready || flyerBusy}>
                  <Download className="h-4 w-4" />
                  {t('design.download_pdf', 'Download PDF')}
                </Button>
                <Button
                  variant="outline"
                  onClick={onFlyerSave}
                  disabled={!ready || flyerBusy || upload.isPending}
                >
                  <Save className="h-4 w-4" />
                  {t('design.save', 'In Mediathek speichern')}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={onDownload} disabled={!ready}>
                  <Download className="h-4 w-4" />
                  {t('design.download', 'Download PNG')}
                </Button>
                <Button variant="outline" onClick={onSave} disabled={!ready || upload.isPending}>
                  <Save className="h-4 w-4" />
                  {t('design.save', 'In Mediathek speichern')}
                </Button>
              </>
            )}
          </div>

          {preparing && (
            <p className="text-xs text-ink-3">
              {t('design.preparing', 'Grafik wird vorbereitet …')}
            </p>
          )}
        </div>

        {/* Preview */}
        <div className="overflow-hidden rounded-xl border border-line bg-paper-2 p-4">
          {format === 'flyer' ? (
            propertyId ? (
              flyerUrl ? (
                <iframe
                  src={flyerUrl}
                  style={{ width: '100%', height: 640, border: 0 }}
                  title="flyer"
                />
              ) : (
                <p className="p-8 text-center text-sm text-ink-3">
                  {t('design.flyer_hint', 'Klicken Sie auf „Vorschau erstellen", um das A4-Exposé zu sehen.')}
                </p>
              )
            ) : (
              <p className="p-8 text-center text-sm text-ink-3">
                {t('design.empty', 'Wählen Sie eine Immobilie für die Vorschau.')}
              </p>
            )
          ) : propertyId ? (
            <div
              style={{
                width: dims.w * previewScale,
                height: dims.h * previewScale,
                position: 'relative',
              }}
            >
              <div
                style={{
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top left',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              >
                <SocialTemplate format={socialFormat} data={data} />
              </div>
            </div>
          ) : (
            <p className="p-8 text-center text-sm text-ink-3">
              {t('design.empty', 'Wählen Sie eine Immobilie für die Vorschau.')}
            </p>
          )}
        </div>
      </div>

      {/* Off-screen export node — only for social formats (square/story), not flyer */}
      {format !== 'flyer' && (
        <div
          style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }}
          aria-hidden
        >
          <SocialTemplate ref={exportRef} format={socialFormat} data={data} />
        </div>
      )}
    </section>
  );
}
